import {
  AnyRole,
  AssignmentScope,
  LocationRole,
  TenantRole,
} from './types';

/**
 * Lifecycle status of an assignment.
 *
 *   active    — user has access
 *   suspended — access temporarily removed (e.g. non-payment); reversible
 *   revoked   — permanently removed; historical record
 */
export type AssignmentStatus = 'active' | 'suspended' | 'revoked';

/**
 * Provenance of an assignment grant — answers "who/what created this role".
 *
 *   manual     — granted by a human operator (admin/owner/manager UI or API).
 *                This is the DEFAULT and the backward-compatible meaning of
 *                any assignment that predates this field (absent ⇒ 'manual').
 *   job_title  — auto-granted by the JobTitle-capability reconciler
 *                ({@link jobTitleRoleReconciliationService}) because the
 *                employee holds an active job assignment whose job title unlocks
 *                a management/books feature. The reconciler is the SOLE owner of
 *                these — it may revoke/replace a `job_title` assignment but must
 *                NEVER touch a `manual` one.
 *   system     — granted by some other automated provisioning path
 *                (onboarding, ownership transfer, migrations). Treated like a
 *                manual grant by the JobTitle reconciler (never auto-revoked).
 *
 * SECURITY INVARIANT: the JobTitle reconciler keys its revoke decisions off
 * this marker. A `manual`/`system`/absent grant is operator/owned intent and
 * is never removed or downgraded by capability changes; only `job_title`
 * grants are auto-maintained.
 */
export type AssignmentSource = 'manual' | 'job_title' | 'system';

/**
 * Unified assignment record on a User document.
 *
 * Stores **current state only**. All lifecycle changes (grant, suspend,
 * unsuspend, revoke) are recorded in the `users/{uid}/assignment_history`
 * subcollection via AssignmentHistoryEntry.
 *
 * A single array (`User.assignments`) holds both tenant-scoped and
 * restaurant-scoped grants. The `scope` field discriminates:
 *
 *   scope === 'tenant'      → role must be a TenantRole, restaurantDocId MUST be undefined.
 *   scope === 'restaurant'  → role must be a RestaurantRole, restaurantDocId MUST be set.
 *
 * Assignment identity is `(scope, tenantDocId, restaurantDocId)` — at most one
 * non-revoked assignment per triple.
 *
 * All identifiers are Firestore document IDs (strings) — never the numeric
 * `tenantId` / `restaurantId`.
 */
export interface Assignment {
  readonly scope: AssignmentScope;

  /** Firestore doc ID of the tenant this assignment applies to. Always required. */
  readonly tenantDocId: string;

  /** Firestore doc ID of the restaurant. Required iff scope === 'restaurant'. */
  readonly restaurantDocId?: string;

  /** Role value — must match `scope` (see invariant above). */
  readonly role: AnyRole;

  /** Lifecycle status: 'active' | 'suspended' | 'revoked'. */
  readonly status: AssignmentStatus;

  /**
   * Derived from status for backward compatibility.
   * true when status === 'active', false otherwise.
   */
  readonly isActive: boolean;

  /** UID of the user who originally granted this assignment. */
  readonly grantedByUid: string;
  readonly grantedAt: Date;

  readonly updatedAt: Date;

  /**
   * Provenance of the grant. Optional for backward compatibility: any
   * assignment written before this field existed has no `grantSource` and
   * MUST be treated as `'manual'` (operator-owned). Only assignments stamped
   * `'job_title'` are auto-maintained by the JobTitle-capability reconciler.
   * See {@link AssignmentSource}.
   */
  readonly grantSource?: AssignmentSource;

  /**
   * Set ONLY on a `grantSource: 'job_title'` assignment that DISPLACED a
   * lower-ranked operator-owned grant, and carries everything needed to put
   * that grant back exactly as it was when the capability is removed.
   *
   * Why this exists: every invited employee holds a `manual` `staff` grant
   * (`grantRestaurantAssignment` defaults `grantSource` to `'manual'` and the
   * invitation path never overrides it), and only ONE assignment may be active
   * per scope. Without recording what was displaced, removing the job title's
   * unlock would leave the person with NO assignment — a lockout — rather than
   * returning them to the role the operator originally gave them.
   *
   * A grant at or above the desired auto-role is NEVER superseded, so this
   * never carries a `manager`/`accountant` operator grant.
   */
  readonly supersedes?: SupersededGrant;
}

/**
 * The operator-owned grant a `job_title` assignment displaced. Restored
 * verbatim by the reconciler when the capability that justified the
 * supersession goes away.
 */
export interface SupersededGrant {
  readonly role: AnyRole;
  readonly grantSource: AssignmentSource;
  readonly grantedByUid: string;
  readonly grantedAt: Date;
}

/**
 * Effective provenance for an assignment, normalizing the backward-compat
 * absence rule: a missing `grantSource` means `'manual'` (operator-owned).
 * Use this everywhere a revoke/override decision depends on provenance so the
 * "absent ⇒ manual" invariant is enforced in exactly one place.
 */
export function effectiveAssignmentSource(a: {
  readonly grantSource?: AssignmentSource;
}): AssignmentSource {
  return a.grantSource ?? 'manual';
}

/**
 * Runtime guard: is this assignment tenant-scoped?
 */
export function isTenantAssignment(
  a: Assignment,
): a is Assignment & { scope: 'tenant'; role: TenantRole } {
  return a.scope === 'tenant';
}

/**
 * Runtime guard: is this assignment restaurant-scoped?
 */
export function isLocationAssignment(
  a: Assignment,
): a is Assignment & {
  scope: 'restaurant';
  role: LocationRole;
  restaurantDocId: string;
} {
  return a.scope === 'restaurant';
}

/**
 * Validate the scope↔role invariant. Throws a plain Error with a precise
 * message. Callers (services, schemas, migration) use this as a final
 * defense against bad data escaping into Firestore.
 */
export function assertAssignmentShape(a: Assignment): void {
  if (a.scope === 'tenant') {
    if (a.role !== 'owner' && a.role !== 'admin') {
      throw new Error(
        `Assignment.assertAssignmentShape: scope='tenant' requires role ∈ {owner, admin}, got '${a.role}'`,
      );
    }
    if (a.restaurantDocId !== undefined) {
      throw new Error(
        `Assignment.assertAssignmentShape: scope='tenant' must not set restaurantDocId (got '${a.restaurantDocId}')`,
      );
    }
  } else if (a.scope === 'restaurant') {
    if (a.role !== 'manager' && a.role !== 'staff' && a.role !== 'accountant') {
      throw new Error(
        `Assignment.assertAssignmentShape: scope='restaurant' requires role ∈ {manager, accountant, staff}, got '${a.role}'`,
      );
    }
    if (!a.restaurantDocId) {
      throw new Error(
        `Assignment.assertAssignmentShape: scope='restaurant' requires restaurantDocId`,
      );
    }
  } else {
    throw new Error(
      `Assignment.assertAssignmentShape: unknown scope '${(a as Assignment).scope}'`,
    );
  }
}

/**
 * Opaque identity string for an assignment — used as React keys, audit refs,
 * and idempotency keys. Active and inactive instances for the same triple
 * share the same identity; callers that need uniqueness across revocations
 * should also include `grantedAt`.
 */
export function assignmentIdentity(a: Assignment): string {
  return a.scope === 'tenant'
    ? `tenant:${a.tenantDocId}`
    : `restaurant:${a.tenantDocId}/${a.restaurantDocId}`;
}
